/**
 * CrisisResourcesTab — persistent floating "Help" tab in the bottom-right
 * corner that opens a sheet listing per-app crisis resources.
 *
 * v3.1 (database-first): Resources are rendered from the crisis_resources
 * Supabase table. The hardcoded SIBLING_APPS registry is fallback-only,
 * used when the DB query fails or returns empty.
 *
 * Source priority: subdomain detection → profile.active_app → profile.primary_app.
 *
 * Hidden on auth routes. Always visible to authenticated members.
 */
import { useLocation } from "react-router-dom";
import { useState, useMemo } from "react";
import { LifeBuoy, Phone, MessageSquare, ExternalLink } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetDescription,
} from "@/components/ui/sheet";
import { useAuth } from "@/lib/auth/AuthProvider";
import { useProfile } from "@/lib/profile/useProfile";
import {
  useCrisisResources,
  type CrisisResource,
} from "@/lib/safety/useCrisisResources";
import {
  GLOBAL_CRISIS_LINES,
  detectSiblingFromHost,
  getSiblingApp,
  type SiblingAppId,
} from "@/lib/sibling-apps";

const HIDE_ROUTES = ["/auth", "/reset-password"];

type RenderableLine = {
  key: string;
  name: string;
  notes: string | null;
  phone: string | null;
  sms: string | null;
  url: string | null;
};

const dbToRenderable = (r: CrisisResource): RenderableLine => ({
  key: r.id,
  name: r.name,
  notes: r.notes,
  phone: r.phone,
  sms: r.sms,
  url: r.url,
});

const CrisisResourcesTab = () => {
  const { session } = useAuth();
  const { profile } = useProfile();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);

  const siblingId = useMemo<SiblingAppId | null>(() => {
    const fromHost = detectSiblingFromHost();
    if (fromHost) return fromHost;
    const fromProfile = (profile?.active_app ?? profile?.primary_app ?? null) as
      | SiblingAppId
      | null;
    return fromProfile;
  }, [profile?.active_app, profile?.primary_app]);

  const sibling = getSiblingApp(siblingId);
  const { global, community, loading, error } = useCrisisResources({
    app: siblingId,
  });

  if (!session) return null;
  if (HIDE_ROUTES.some((r) => pathname.startsWith(r))) return null;

  // Database is the source of truth. Fall back to hardcoded registries
  // only if the DB query failed OR returned zero rows.
  const dbAvailable = !error && (global.length > 0 || community.length > 0);

  const globalLines: RenderableLine[] = dbAvailable
    ? global.map(dbToRenderable)
    : GLOBAL_CRISIS_LINES.map((line, i) => ({
        key: `fallback-global-${i}-${line.name}`,
        name: line.name,
        notes: line.notes ?? null,
        phone: line.phone ?? null,
        sms: line.sms ?? null,
        url: null,
      }));

  const communityLines: RenderableLine[] = dbAvailable
    ? community.map(dbToRenderable)
    : (sibling?.crisis ?? []).map((line, i) => ({
        key: `fallback-${sibling?.id ?? "none"}-${i}-${line.name}`,
        name: line.name,
        notes: line.notes ?? null,
        phone: line.phone ?? null,
        sms: line.sms ?? null,
        url: line.url ?? null,
      }));

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          type="button"
          aria-label="Crisis resources"
          className="fixed z-40 bottom-24 right-6 md:bottom-6 md:right-24 inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-2 text-xs font-medium shadow-lg transition hover:bg-accent"
        >
          <LifeBuoy className="h-4 w-4 text-primary" />
          Help
        </button>
      </SheetTrigger>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle>
            Crisis resources{sibling ? ` · ${sibling.name}` : ""}
          </SheetTitle>
          <SheetDescription>
            You don't have to face this alone. These lines are free,
            confidential, and open right now.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-3">
          {loading && (
            <p className="text-xs text-muted-foreground">Loading resources…</p>
          )}

          {!loading &&
            globalLines.length === 0 &&
            communityLines.length === 0 && (
              <article className="rounded-xl border border-border bg-card p-3">
                <p className="font-medium text-sm">
                  If you are in immediate danger, call your local emergency
                  number.
                </p>
                <p className="mt-2 text-xs text-muted-foreground">
                  Resources couldn't be loaded right now. In the US, you can
                  call or text 988 for the Suicide & Crisis Lifeline.
                </p>
              </article>
            )}

          {!loading &&
            globalLines.length > 0 &&
            globalLines.map((line) => (
              <article
                key={line.key}
                className="rounded-xl border border-border bg-card p-3"
              >
                <p className="font-medium text-sm">{line.name}</p>
                {line.notes && (
                  <p className="text-xs text-muted-foreground">{line.notes}</p>
                )}
                <div className="mt-2 flex flex-wrap gap-3 text-xs text-primary">
                  {line.phone && (
                    <a
                      href={`tel:${line.phone.replace(/[^\d+]/g, "")}`}
                      className="inline-flex items-center gap-1"
                    >
                      <Phone className="h-3 w-3" /> Call {line.phone}
                    </a>
                  )}
                  {line.sms && (
                    <a
                      href={`sms:${line.sms}?body=HOME`}
                      className="inline-flex items-center gap-1"
                    >
                      <MessageSquare className="h-3 w-3" /> Text {line.sms}
                    </a>
                  )}
                  {line.url && (
                    <a
                      href={line.url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1"
                    >
                      <ExternalLink className="h-3 w-3" /> Web
                    </a>
                  )}
                </div>
              </article>
            ))}

          {!loading && communityLines.length > 0 && (
            <>
              <p className="pt-2 text-[10px] uppercase tracking-wider text-muted-foreground">
                {sibling?.name ?? "Community"} resources
              </p>
              {communityLines.map((line) => (
                <article
                  key={line.key}
                  className="rounded-xl border border-border bg-card p-3"
                >
                  <p className="font-medium text-sm">{line.name}</p>
                  {line.notes && (
                    <p className="text-xs text-muted-foreground">
                      {line.notes}
                    </p>
                  )}
                  <div className="mt-2 flex flex-wrap gap-3 text-xs text-primary">
                    {line.phone && (
                      <a
                        href={`tel:${line.phone.replace(/[^\d+]/g, "")}`}
                        className="inline-flex items-center gap-1"
                      >
                        <Phone className="h-3 w-3" /> {line.phone}
                      </a>
                    )}
                    {line.sms && (
                      <a
                        href={`sms:${line.sms}`}
                        className="inline-flex items-center gap-1"
                      >
                        <MessageSquare className="h-3 w-3" /> Text {line.sms}
                      </a>
                    )}
                    {line.url && (
                      <a
                        href={line.url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1"
                      >
                        <ExternalLink className="h-3 w-3" /> Web
                      </a>
                    )}
                  </div>
                </article>
              ))}
            </>
          )}

          <p className="pt-3 text-[11px] text-muted-foreground">
            If you are in immediate danger, call your local emergency number.
          </p>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default CrisisResourcesTab;
